import type { BinaryPlanningPageQuery } from '../../../cpp-backend/initialData';
import { arrayParser } from './array';
import { numericParser } from './numeric';
import { uuidParser } from './uuid';

const BOOL = 16;
const INT8 = 20;
const INT4 = 23;
const TEXT = 25;
const JSONOID = 114;
const FLOAT8OID = 701;
const INTARRAY = 1007;
const TEXTARRAY = 1009;
const VARCHAR = 1043;
const TIME = 1083;
const TIMESTAMP = 1114;
const TIMESTAMPTZ = 1184;
const NUMERIC = 1700;
const UUID = 2950;
const JSONB = 3802;

const decoder = new TextDecoder('utf-8');
const textParser = (dv: DataView) => decoder.decode(dv);

const i64Parser = (dv: DataView) => String(dv.getBigInt64(0));

const f64Parser = (dv: DataView) => dv.getFloat64(0);

const bigintParser = (dv: DataView) => dv.getBigInt64(0);

const i32Parser = (dv: DataView) => dv.getInt32(0);

const boolParser = (dv: DataView) => dv.getUint8(0) !== 0;

const padZero = (v: number) => (v > 9 ? v : '0' + v);

const dateToTimeString = (d: Date): string =>
  `${padZero(d.getHours())}:${padZero(d.getMinutes())}:${padZero(d.getSeconds())}`;

// TODO double check that this takes y2k-epoch offset into account
const timeParser = (dv: DataView): Date | number | string =>
  dateToTimeString(new Date(Number(bigintParser(dv) / 1000n)));

// discard usecs and shift from 2000 to 1970
const epoch = 946684800000;

// convert Postgres timestamptz to ISO String
export function toDateString(dv: DataView): string {
  return new Date(epoch + Number(dv.getBigInt64(0) / 1000n)).toISOString();
}

// convert Postgres timestamp to ISO String
export function toNoTzDateString(dv: DataView): string | null {
  return toDateString(dv).slice(0, -1);
}

type ValueOrArray = null | number | string | ValueOrArray[];

const parsers: {
  [type: number | string]:
    | undefined
    | ((dv: DataView) => string | number | Date | boolean | ValueOrArray);
} = {
  [BOOL]: boolParser,
  [INT4]: i32Parser,
  [INT8]: i64Parser,
  [TEXT]: textParser,
  [JSONOID]: textParser,
  [FLOAT8OID]: f64Parser,
  [INTARRAY]: arrayParser,
  [TEXTARRAY]: arrayParser,
  [VARCHAR]: textParser,
  [TIME]: timeParser,
  [TIMESTAMP]: toNoTzDateString,
  [TIMESTAMPTZ]: toDateString,
  [NUMERIC]: numericParser,
  [UUID]: uuidParser,
  [JSONB]: textParser,
};

const type_lengths: {
  [type: number | string]: undefined | number;
} = {
  [BOOL]: 1,
  [INT4]: 4,
  [INT8]: 8,
  [TIME]: 8,
  [TIMESTAMP]: 8,
  [TIMESTAMPTZ]: 8,
  [UUID]: 16,
};

function text(binary: ArrayBuffer, i: number, name_length: number) {
  return textParser(new DataView(binary, i, name_length));
}

function u8(dv: DataView, i: number) {
  return dv.getUint8(i);
}

function i8(dv: DataView, i: number) {
  return dv.getInt8(i);
}

function u16(dv: DataView, i: number) {
  return dv.getUint16(i, true);
}

function u32(dv: DataView, i: number) {
  return dv.getUint32(i, true);
}

function i32(dv: DataView, i: number) {
  return dv.getInt32(i, true);
}

export function parse(binary: ArrayBuffer) {
  const result: Partial<BinaryPlanningPageQuery> = {};
  const dv = new DataView(binary);
  let table_name;
  let i = 0;

  const queried_views = [
    'latest_port_cost_by_port_and_primary_attribute',
    'port_costs_by_port_and_primary_attribute',
    'port_costs_by_port_and_vessel_name',
  ];

  try {
    while (true) {
      const table_name_length = u8(dv, i);
      if (table_name_length === 0xff) {
        break;
      }
      i++;

      table_name = text(binary, i, table_name_length) || queried_views.shift();
      i += table_name_length;

      const num_rows = i32(dv, i);
      i += 4;

      const num_cols = i8(dv, i);
      i++;

      const types = [];
      const parse = [];
      const lengths = [];
      const nullable = [];
      const column_names = [];
      const const_length = [];
      for (let j = 0; j < num_cols; j++) {
        const type = u32(dv, i);
        i += 4;

        const column_data = i8(dv, i);
        i++;

        const is_nullable = (column_data & 128) !== 0;
        const is_const_length = (column_data & 64) !== 0;
        const name_length = column_data & 63;
        const name = text(binary, i, name_length);
        i += name_length;

        const parser = parsers[type] || parsers[name];
        if (!parser) {
          throw new Error(`no parser for column: ${name} type: ${type}`);
        }
        const length = type_lengths[type];

        types.push(type);
        parse.push(parser);
        lengths.push(length);
        column_names.push(name);
        nullable.push(is_nullable);
        const_length.push(is_const_length);

        if (is_const_length !== (length !== undefined)) {
          throw new Error(
            `differing length definition: ${table_name} ${name} ${type}`,
          );
        }
      }

      const tableName = table_name as keyof BinaryPlanningPageQuery;
      if (!result[tableName]) {
        result[tableName] = [];
      }
      const rows = result[tableName];
      type RowType = (typeof rows)[number];
      for (let r = 0; r < num_rows; r++) {
        const row: Partial<RowType> = {};
        type ColumnName = keyof RowType;
        for (let c = 0; c < num_cols; c++) {
          const name = column_names[c] as ColumnName;
          const const_length = lengths[c];
          const is_nullable = nullable[c];
          if (is_nullable && const_length) {
            const is_null = u8(dv, i) === 255;
            i++;
            if (is_null) {
              row[name] = null as unknown as RowType[typeof name];
            } else {
              const parser = parse[c];
              row[name] = parser(
                new DataView(binary, i, const_length),
              ) as unknown as RowType[typeof name];
              i += const_length;
            }
          } else if (is_nullable && u8(dv, i) === 255) {
            i++;
            row[name] = null as unknown as RowType[typeof name];
          } else {
            const length = const_length || u16(dv, i);
            if (!const_length) {
              i += 2;
            }

            const parser = parse[c];
            row[name] = parser(
              new DataView(binary, i, length),
            ) as unknown as RowType[typeof name];
            i += length;
          }
        }
        rows[r] = row as RowType;
      }
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
  return result;
}
